/**
 * TrajectoryChart — the stickiness hook. Plots the target's readiness over time
 * (readiness = 100 − gap; initial gap_analysis + each monthly check-in). Renders
 * nothing until there are at least two points, so it appears once the user has
 * tracked progress at least once — giving them a reason to come back.
 */

import { useQuery } from "@tanstack/react-query";
import { Box, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getResumeTrajectory } from "../../api/client";

// coral via theme.palette.primary — per-product accent deferred (spec D2)
export default function TrajectoryChart({ targetId }: { targetId: string }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const { data } = useQuery({
    queryKey: ["resume", "trajectory", targetId],
    queryFn: () => getResumeTrajectory(targetId),
  });

  const points = data?.points ?? [];
  if (points.length < 2) return null; // need ≥2 points to show a trend

  const chartData = points.map((p) => ({
    name: new Date(p.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    readiness: 100 - p.gap_score,
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={500}>
        {t("TrajectoryChart.title")}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t("TrajectoryChart.subtitle")}
      </Typography>
      <Box sx={{ height: 220, mt: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="name" fontSize={12} />
            <YAxis domain={[0, 100]} fontSize={12} />
            <Tooltip />
            <Line type="monotone" dataKey="readiness" stroke={theme.palette.primary.light} strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
